import React, { ChangeEvent, ReactNode } from 'react';

import { Button } from '@/components';
import { useTranslation } from 'react-i18next';
import { SignupDataType } from '@/utils/auth';

import styles from '../Signup.module.css';

interface RadioButtonProps {
  id: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  children: ReactNode;
}

function RadioButton({ id, name, value, checked, onChange, children }: RadioButtonProps) {
  return (
    <label htmlFor={id} className={`${styles.radioWrap} ${checked ? styles.checked : ''}`}>
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className={styles.radioInput}
      />
      {children}

      {checked && (
        <svg
          className={styles.radioWrapCheck}
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M26.6663 8L11.9997 22.6667L5.33301 16"
            stroke="#6D5AED"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </label>
  );
}

interface Step1Props {
  data: SignupDataType;
  onNext: () => void;
  onChange: (data: SignupDataType) => void;
}

function Step1({ data, onNext, onChange }: Step1Props) {
  const { t } = useTranslation();

  const handleRoleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...data, account_type: event.target.value });
  };

  return (
    <div>
      <h3 className={styles.signupTitle}>{t('signup.step1.title')}</h3>
      <RadioButton
        name="role"
        id="role_advertiser"
        value="advertiser"
        checked={data.account_type === 'advertiser'}
        onChange={handleRoleChange}
      >
        <svg
          width="60"
          height="60"
          viewBox="0 0 60 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M26.2834 10.5V10.5048C26.2834 10.5 26.2834 10.5 26.281 10.5C22.6559 10.5 19.7178 13.4406 19.7178 17.0657V20.4713H14.688H14.6856C14.1633 20.4713 13.7817 20.891 13.7125 21.3966L10.5287 44.6042C10.5097 44.764 10.5001 44.9262 10.5001 45.0907C10.5001 47.3921 12.3651 49.2571 14.6665 49.2571L46.5858 49.2762C48.1885 49.269 49.4882 47.9836 49.5001 46.3809V36.0067C49.4882 34.4041 48.1885 33.1162 46.5858 33.1115H40.464L38.8566 21.3801C38.828 20.8649 38.3987 20.4714 37.8787 20.4714H32.849V17.0658C32.849 13.4384 29.9085 10.5 26.2834 10.5ZM26.2834 12.4485V12.4508L26.2858 12.4532C28.8328 12.4508 30.8982 14.5162 30.8982 17.0656V20.4712H21.6687V17.0656C21.6687 14.5162 23.7339 12.4485 26.2834 12.4485ZM38.2553 35.062V35.0644H42.1117V37.4803L40.6521 36.7958L40.6474 36.7982C40.5067 36.7195 40.3469 36.6789 40.1823 36.6789C40.044 36.6789 39.9033 36.7075 39.7697 36.7719L38.2553 37.4803L38.2553 35.062ZM38.4938 33.1112V33.1135L33.7837 33.1112V33.1135C32.1786 33.1159 30.8789 34.4038 30.867 36.0064V46.3806C30.8693 46.705 30.9242 47.0174 31.0244 47.3083H14.6499C13.4336 47.2988 12.451 46.309 12.451 45.0904C12.451 44.9664 12.4605 44.8423 12.4796 44.7255L15.537 22.4221H19.7178V24.8571C19.7178 25.3961 20.1566 25.8325 20.6932 25.8325C21.2322 25.8325 21.6687 25.3961 21.6687 24.8571V22.4221H30.8981V24.8571C30.8981 25.3961 31.3345 25.8325 31.8735 25.8325C32.4101 25.8325 32.849 25.3961 32.849 24.8571V22.4221H37.0297L38.4938 33.1112ZM33.7669 35.062V35.0644L36.3069 35.062V39.0138C36.3069 39.3477 36.4762 39.6577 36.7576 39.8366C36.9174 39.9368 37.0987 39.9892 37.2823 39.9892C37.423 39.9892 37.5637 39.9582 37.6949 39.8962L40.1823 38.73V38.7324L42.6721 39.8962C42.8057 39.9582 42.9464 39.9892 43.0871 39.9892C43.2684 39.9892 43.452 39.9391 43.6094 39.8365C43.8908 39.6577 44.0625 39.3476 44.0625 39.0137V35.0619H46.6C47.1199 35.0762 47.5397 35.4984 47.5492 36.0183V46.3686C47.5397 46.8885 47.1199 47.3107 46.6 47.325H33.7666C33.2467 47.3107 32.8269 46.8885 32.8174 46.3686V36.0183C32.827 35.4984 33.247 35.0763 33.7669 35.062Z"
            fill="black"
          />
        </svg>
        <span className={styles.radioLabel}>{t('signup.type.advertiser')}</span>
      </RadioButton>
      <RadioButton
        name="role"
        id="role_influencer"
        value="creator"
        checked={data.account_type === 'creator'}
        onChange={handleRoleChange}
      >
        <svg
          width="60"
          height="60"
          viewBox="0 0 60 60"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g opacity="0.8">
            <path
              d="M11.2561 32.1869C11.762 34.1678 13.4243 35.5484 15.3367 35.8041C15.8611 40.5666 17.8384 45.0013 21.2572 48.9966C21.9058 49.7546 22.8712 50.1771 23.8663 50.1771C24.1554 50.1771 24.4482 50.1419 24.7336 50.0677C25.8621 49.7786 26.7683 48.9503 27.1612 47.8495C27.5429 46.7822 27.3743 45.6073 26.7109 44.7067C25.864 43.5577 25.1543 42.368 24.5557 41.1468C26.6034 40.2889 28.2249 38.6489 29.0513 36.59C32.2276 37.2034 36.0302 37.7186 39.8847 37.7186C41.8823 37.7186 43.8929 37.5814 45.8331 37.2479C45.8442 37.246 45.8516 37.2386 45.8628 37.2368C45.8813 37.2331 45.9017 37.2331 45.9184 37.2294C47.7307 36.7661 48.95 34.7999 49.3522 31.6922C49.7098 28.9311 49.4133 25.4823 48.5183 21.978C46.8394 15.4033 42.9588 8.96909 39.0342 9.82727C39.0212 9.82912 39.0082 9.82356 38.9952 9.82727C38.6172 9.89769 29.6553 11.6378 18.3462 22.5582L13.8876 23.9851C11.4359 24.7689 10.0146 27.3392 10.6521 29.8315L11.2543 32.1868L11.2561 32.1869ZM24.9211 46.0241C25.1472 46.3335 25.2009 46.7264 25.0675 47.1007C24.923 47.5047 24.5931 47.8086 24.1817 47.9123C23.7166 48.0291 23.2329 47.8864 22.9457 47.5491C19.8343 43.9152 18.0333 39.8976 17.5475 35.6038L20.3754 34.8811C20.8757 38.8487 22.3952 42.592 24.921 46.0219L24.9211 46.0241ZM23.6721 39.0955C23.1903 37.7798 22.8438 36.4307 22.6381 35.0502C23.6573 35.3467 25.0842 35.734 26.8169 36.125C26.1794 37.4481 25.0786 38.5025 23.6703 39.0955H23.6721ZM37.6948 15.6665C37.9598 13.6169 38.6418 12.2104 39.4738 11.9992C39.5572 11.9788 39.6424 11.9677 39.7351 11.9677C41.4603 11.9677 44.6309 15.7387 46.3671 22.5287C47.1936 25.7642 47.4734 28.9183 47.1491 31.407C46.886 33.438 46.2151 34.8316 45.3943 35.065C45.3312 35.0761 45.2645 35.0817 45.1997 35.091C43.9673 35.1966 41.916 33.2341 40.2057 29.5021C41.3398 29.0833 42.2997 28.3198 42.9261 27.2654C43.6451 26.0516 43.8489 24.6303 43.5005 23.2627C42.8278 20.6294 40.2687 18.9931 37.6356 19.3675C37.5429 18.0406 37.5503 16.7805 37.693 15.6668L37.6948 15.6665ZM39.3737 27.4486C39.0495 26.5498 38.7474 25.5881 38.4806 24.5448C38.223 23.533 38.0377 22.5415 37.8876 21.565C39.4349 21.313 40.9526 22.2655 41.3473 23.811C41.5493 24.6023 41.4326 25.425 41.0138 26.1273C40.6394 26.7574 40.0594 27.2114 39.3719 27.4467L39.3737 27.4486ZM14.5699 26.1033L19.2768 24.5948C19.4399 24.5411 19.59 24.4521 19.7123 24.3317C26.4986 17.7328 32.4541 14.5786 35.9917 13.135C35.7693 13.7984 35.597 14.5434 35.4914 15.3772C35.1337 18.1384 35.4302 21.589 36.3253 25.0915C37.3667 29.1702 39.2587 33.1875 41.4805 35.4542C32.0296 35.8285 22.0303 32.5374 21.73 32.4392C21.704 32.428 21.6744 32.428 21.6447 32.4206C21.5373 32.391 21.4298 32.3799 21.3223 32.3817C21.3056 32.3817 21.2926 32.3743 21.276 32.3761C21.2537 32.378 21.2352 32.391 21.213 32.3947C21.1666 32.4002 21.1222 32.3984 21.0758 32.4095L16.6339 33.5436C15.2218 33.9068 13.7746 33.0488 13.4132 31.6349L12.8109 29.2796C12.4644 27.925 13.2372 26.5277 14.5695 26.1015L14.5699 26.1033Z"
              fill="black"
            />
          </g>
        </svg>
        <span className={styles.radioLabel}>{t('signup.type.influencer')}</span>
      </RadioButton>

      <div className={styles.signupButtonWrap}>
        <Button disabled={!data.account_type} theme="cta" onClick={onNext}>
          {t('signup.next')}
        </Button>
      </div>
    </div>
  );
}

export default Step1;
